import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import { useUIStore } from '@/store';

function ConversationForkOrigin({
  conversationId,
  messageId,
  title,
  isCompact = false,
}) {
  const router = useRouter();
  const { colors } = useTheme();
  const requestScrollToMessage = useUIStore(state => state.requestScrollToMessage);

  const openSource = useCallback(() => {
    router.push(`/c/${conversationId}`);
    if (messageId) {
      setTimeout(() => {
        requestScrollToMessage({
          conversationId,
          messageId,
          viewPosition: 0.5,
        });
      }, 500);
    }
  }, [router, conversationId, messageId, requestScrollToMessage]);

  if (!conversationId) return null;

  return (
    <View
      style={[styles.container, isCompact && styles.containerCompact]}
      accessibilityRole="summary"
      accessibilityLabel={`Branched from ${title || 'original chat'}`}
    >
      <View style={[styles.line, { backgroundColor: colors.border_color_secondary }]} />
      <View style={styles.label}>
        <Text size="xs" color="tertiary">Branched from </Text>
        <Pressable
          onPress={openSource}
          accessibilityRole="link"
          accessibilityLabel={`Open ${title || 'original chat'}`}
        >
          <Text
            size="xs"
            color="secondary"
            weight="medium"
            style={styles.link}
            numberOfLines={1}
          >
            {title || 'original chat'}
          </Text>
        </Pressable>
      </View>
      <View style={[styles.line, { backgroundColor: colors.border_color_secondary }]} />
    </View>
  );
}

ConversationForkOrigin.propTypes = {
  conversationId: PropTypes.string.isRequired,
  messageId: PropTypes.string,
  title: PropTypes.string,
  isCompact: PropTypes.bool,
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: 900,
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingHorizontal: 20,
    marginTop: 24,
    marginBottom: 12,
  },
  containerCompact: {
    gap: 8,
    paddingHorizontal: 12,
    marginTop: 16,
    marginBottom: 8,
  },
  line: {
    flex: 1,
    height: StyleSheet.hairlineWidth,
  },
  label: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  link: {
    textDecorationLine: 'underline',
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
});

export default React.memo(ConversationForkOrigin);
